package app.footyguide.core.theme

import kotlin.math.pow
import kotlin.math.round

/**
 * Cores derivadas do c1/c2 de um clube. Único motor — mobile e web consomem
 * esta mesma implementação, nunca reimplementam o cálculo de contraste.
 */
data class ClubColors(
    val c1: String,
    val fill: String,
    val onFill: String,
    val stripe: List<String>,
    val heroBg: List<String>,
)

object ClubColorEngine {
    private const val INK = "#16181D"
    private const val WHITE = "#FFFFFF"

    const val CONTRAST_THRESHOLD = 4.5
    const val TINT_ALPHA = 0.08f
    const val BACKING_ALPHA = 0.12f
    const val HOVER_ALPHA = 0.14f
    const val BORDER_ALPHA = 0.32f

    fun derive(c1: String, c2: String? = null): ClubColors {
        var fill = c1
        val contrastWhite = contrast(fill, WHITE)
        val contrastInk = contrast(fill, INK)
        var onFill = if (contrastWhite >= contrastInk) WHITE else INK

        // Tons médios (ex.: celeste do Grêmio) não passam 4.5:1 nem com branco
        // nem com tinta — escurece o fill até passar, sem trocar a identidade.
        if (maxOf(contrastWhite, contrastInk) < CONTRAST_THRESHOLD) {
            var t = 0.05
            while (t <= 0.75) {
                val candidate = mix(c1, INK, t)
                if (contrast(candidate, WHITE) >= CONTRAST_THRESHOLD) {
                    fill = candidate
                    onFill = WHITE
                    break
                }
                t += 0.05
            }
        }

        val isRealColor = c2 != null && c2.uppercase() != WHITE && c2.uppercase() != "#000000"
        return ClubColors(
            c1 = c1,
            fill = fill,
            onFill = onFill,
            stripe = if (isRealColor) listOf(c1, c2!!) else listOf(c1),
            heroBg = if (isRealColor) listOf(fill, mix(c2!!, INK, 0.15)) else listOf(fill),
        )
    }

    internal fun contrast(a: String, b: String): Double {
        val l1 = luminance(a)
        val l2 = luminance(b)
        return (maxOf(l1, l2) + 0.05) / (minOf(l1, l2) + 0.05)
    }

    internal fun mix(a: String, b: String, t: Double): String {
        val (ar, ag, ab) = hexToRgb(a)
        val (br, bg, bb) = hexToRgb(b)
        return rgbToHex(
            round(ar + (br - ar) * t).toInt(),
            round(ag + (bg - ag) * t).toInt(),
            round(ab + (bb - ab) * t).toInt(),
        )
    }

    private fun luminance(hex: String): Double {
        val (r, g, b) = hexToRgb(hex)
        fun channel(v: Int): Double {
            val c = v / 255.0
            return if (c <= 0.03928) c / 12.92 else ((c + 0.055) / 1.055).pow(2.4)
        }
        return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
    }

    private fun hexToRgb(hex: String): Triple<Int, Int, Int> {
        val clean = hex.removePrefix("#")
        return Triple(
            clean.substring(0, 2).toInt(16),
            clean.substring(2, 4).toInt(16),
            clean.substring(4, 6).toInt(16),
        )
    }

    private fun rgbToHex(r: Int, g: Int, b: Int): String {
        fun clamp(v: Int) = v.coerceIn(0, 255)
        fun toHexByte(v: Int) = v.toString(16).padStart(2, '0').uppercase()
        return "#${toHexByte(clamp(r))}${toHexByte(clamp(g))}${toHexByte(clamp(b))}"
    }
}
